import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import request from '@dashboard/services/request';
import { apiUrl } from '@dashboard/services/env';
import { sellerBrandCacheKey } from '@dashboard/lib/queryKeys';

/**
 * Hook for fetching brand data during onboarding.
 *
 * @param {Object} options
 * @param {boolean} [options.autoStart=false] - Start fetching immediately on mount
 * @param {string} [options.fallbackUrl=null] - URL to fetch from if cache is empty/failed
 * @param {number} [options.pollingInterval=2000] - Polling interval in milliseconds
 * @returns {Object} Hook state and methods
 */
export default function useBrandFetch(options = {}) {
  const {
    autoStart = false,
    fallbackUrl = null,
    pollingInterval = 1000,
    maxPollRetries = 15,
  } = options;

  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();
  const hasFetchedFallbackRef = useRef(false);
  const [hasFetchTriggered, setHasFetchTriggered] = useState(false);
  const pollCountRef = useRef(0);
  const [isTimedOut, setIsTimedOut] = useState(false);

  const cacheQuery = useQuery({
    queryKey: sellerBrandCacheKey(sellerId),
    queryFn: async () => {
      pollCountRef.current += 1;
      const response = await request(`${apiUrl}/sellers/${sellerId}/brand/cached`, token);
      if (response?.meta?.status === 404) {
        // Preserve existing client cache if server has no data
        const existingData = queryClient.getQueryData(sellerBrandCacheKey(sellerId));
        return existingData || null;
      }
      // Never downgrade from completed to pending (protects against out-of-order responses)
      if (response?.status === 'pending') {
        const existingData = queryClient.getQueryData(sellerBrandCacheKey(sellerId));
        if (existingData?.status === 'completed') {
          return existingData;
        }
      }
      return response;
    },
    enabled: (autoStart || hasFetchTriggered) && !!sellerId && !!token,
    refetchInterval: query => {
      const data = query.state.data;
      if (data?.status === 'pending') {
        if (pollCountRef.current >= maxPollRetries) {
          setIsTimedOut(true);
          return false;
        }
        return pollingInterval;
      }
      return false;
    },
    staleTime: 0,
  });

  const fetchMutation = useMutation({
    mutationFn: async url => {
      if (!sellerId || !token || !url) {
        throw new Error('Missing required params');
      }
      // Fire and forget - don't await response, let polling pick it up.
      // When the response arrives, invalidate the cache so a fresh /cached
      // fetch picks up the completed result (in case polling already stopped).
      request(`${apiUrl}/sellers/${sellerId}/brand/fetch`, token, {
        method: 'POST',
        body: JSON.stringify({ url }),
      })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: sellerBrandCacheKey(sellerId) });
        })
        .catch(() => {});
    },
    onSuccess: () => {
      pollCountRef.current = 0;
      setIsTimedOut(false);
      setHasFetchTriggered(true);
      queryClient.setQueryData(sellerBrandCacheKey(sellerId), { status: 'pending' });
    },
  });

  const cachedResult = cacheQuery.data?.status === 'completed' ? cacheQuery.data.result : null;
  const brandData = cachedResult;

  const getStatus = () => {
    if (fetchMutation.isPending) return 'loading';
    if (fetchMutation.isError) return 'failed';
    if (cacheQuery.isLoading) return 'loading';
    if (cacheQuery.isError) return 'failed';
    if (isTimedOut) return 'failed';
    if (cacheQuery.data?.status === 'pending') return 'polling';
    if (cacheQuery.data?.status === 'completed') return 'completed';
    if (cacheQuery.data?.status === 'failed') return 'failed';
    return 'idle';
  };

  const status = getStatus();
  const error = fetchMutation.error?.message || cacheQuery.error?.message || null;

  const fetchBrand = useCallback(
    url => {
      fetchMutation.mutate(url);
    },
    [fetchMutation],
  );

  // Initialize (for backwards compatibility)
  const initialize = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: sellerBrandCacheKey(sellerId) });
  }, [queryClient, sellerId]);

  // Stop polling (no-op now, react-query handles it)
  const stopPolling = useCallback(() => {
    // No-op - react-query handles polling automatically
  }, []);

  // Auto-fetch from fallbackUrl when cache is empty or failed
  useEffect(() => {
    // Only trigger fallback if:
    // - We have a fallbackUrl
    // - Cache query has completed (not loading)
    // - Cache is either null (404) or failed
    // - We haven't already fetched the fallback
    // - Mutation isn't already in progress or completed
    const shouldFetchFallback =
      fallbackUrl &&
      cacheQuery.isFetched &&
      !cacheQuery.isLoading &&
      (cacheQuery.data === null || cacheQuery.data?.status === 'failed') &&
      !hasFetchedFallbackRef.current &&
      !fetchMutation.isPending &&
      !fetchMutation.isSuccess;

    if (shouldFetchFallback) {
      hasFetchedFallbackRef.current = true;
      fetchMutation.mutate(fallbackUrl);
    }
  }, [fallbackUrl, cacheQuery.isFetched, cacheQuery.isLoading, cacheQuery.data, fetchMutation]);

  const resetWithRef = useCallback(() => {
    hasFetchedFallbackRef.current = false;
    pollCountRef.current = 0;
    setIsTimedOut(false);
    setHasFetchTriggered(false);
    queryClient.removeQueries({ queryKey: sellerBrandCacheKey(sellerId) });
    fetchMutation.reset();
  }, [queryClient, sellerId, fetchMutation]);

  return {
    brandData,
    status,
    error,

    isLoading: status === 'loading' || status === 'polling',
    isPolling: status === 'polling',
    isCompleted: status === 'completed',
    isFailed: status === 'failed',
    isIdle: status === 'idle',
    isFetchedAfterMount: cacheQuery.isFetchedAfterMount,

    fetchBrand,
    stopPolling,
    reset: resetWithRef,
    initialize,
  };
}
